import React, { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker";
import Draggable from "react-draggable";
import { MdDelete } from "react-icons/md";

import AppFileSelect from "./AppFileSelect";
import AppButton from "./AppButton";
import { useWindowWidth } from "../hooks/useWindowWidth";
import AppSpinner from "./Spinner";
import TitleHeading from "./TitleHeading";
import { logOperationSuccess } from "../lib/analytics";

const PDFOrganizer = () => {
	const [pdfDoc, setPdfDoc] = useState(null);
	const [pdfPages, setPdfPages] = useState([]);
	const [gridCols, setGridCols] = useState(); // Number of columns in the grid
	const gridSize = 210; // Size of each grid cell (100px x 100px)
	const { windowWidth: width, windowHeight: height } = useWindowWidth();

	const [draggedBoxId, setDraggedBoxId] = useState(null); // ID of the currently dragged box
	const [isLoading, setIsLoading] = useState(false); // Loading state

	useEffect(() => {
		if (width <= 430) {
			setGridCols(1);
		}

		if (width <= 650 && width > 430) {
			setGridCols(2);
		}

		if (width <= 863 && width > 650) {
			setGridCols(3);
		}

		if (width <= 1077 && width > 863) {
			setGridCols(4);
		}

		if (width <= 1293 && width > 1077) {
			setGridCols(5);
		}

		if (width <= 1507 && width > 1293) {
			setGridCols(6);
		}

		if (width <= 1675 && width > 1507) {
			setGridCols(7);
		}

		if (width <= 1889 && width > 1675) {
			setGridCols(8);
		}

		if (width > 1889) {
			setGridCols(9);
		}
	}, [width]);

	// Load PDF from file input
	const loadPDF = async (file) => {
		if (!file) return;

		setIsLoading(true); // Set loading state
		const arrayBuffer = await file.arrayBuffer();
		const pdf = await PDFDocument.load(arrayBuffer);
		setPdfDoc(pdf);

		// Load using pdfjs for rendering thumbnails
		const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
		const pdfJsDoc = await loadingTask.promise;

		const pages = await Promise.all(
			pdf.getPages().map(async (page, index) => {
				const thumbnail = await renderThumbnail(pdfJsDoc, index);
				return {
					id: index + 1,
					index,
					page,
					width: page.getWidth(),
					height: page.getHeight(),
					thumbnail,
				};
			})
		);

		setPdfPages(pages);

		setIsLoading(false); // Reset loading state
	};

	// Get x, y position from the index
	const getPositionFromIndex = (index) => ({
		x: (index % gridCols) * gridSize,
		y: Math.floor(index / gridCols) * gridSize,
	});

	// Handle dragging over the grid to shift other boxes
	const handleDrag = (e, data, draggedPageId) => {
		const draggedPage = pdfPages.find((page) => page.id === draggedPageId);
		const draggedIndex = draggedPage.index;

		const targetX = data.x;
		const targetY = data.y;

		// Calculate the target index based on the x and y position
		const targetIndex =
			Math.round(targetX / gridSize) +
			gridCols * Math.round(targetY / gridSize);

		if (
			targetIndex >= 0 &&
			targetIndex < pdfPages.length &&
			targetIndex !== draggedIndex
		) {
			const updatedPdfPages = [...pdfPages]
				.filter((p) => p.id !== draggedPageId)
				.map((pg) => {
					if (
						draggedIndex < targetIndex &&
						pg.index > draggedIndex &&
						pg.index <= targetIndex
					) {
						return { ...pg, index: pg.index - 1 }; // Shift pges backward
					} else if (
						draggedIndex > targetIndex &&
						pg.index >= targetIndex &&
						pg.index < draggedIndex
					) {
						return { ...pg, index: pg.index + 1 }; // Shift pges forward
					}
					return pg;
				});

			setPdfPages(
				[...updatedPdfPages, { ...draggedPage, index: targetIndex }].sort(
					(a, b) => a.index - b.index
				)
			);
		}
	};

	// Handle the stop event to finalize the box's position
	const handleStop = () => {
		setDraggedBoxId(null); // Reset the dragged box
	};

	// Delete a box and rearrange the grid
	const handleDelete = (pageId) => {
		setPdfPages((prev) => {
			// Remove the selected box and reindex the remaining boxes
			const updatedPages = prev
				.filter((pg) => pg.id !== pageId)
				.map((pg2, idx) => ({ ...pg2, index: idx }));
			return updatedPages;
		});
	};

	// Add a new blank page
	const addBlankPage = async () => {
		if (!pdfDoc) return;

		const newPage = pdfDoc.addPage();
		const newPageObj = {
			id: pdfPages.length + 1,
			index: pdfPages.length,
			page: newPage,
			width: newPage.getWidth(),
			height: newPage.getHeight(),
			thumbnail: "./assets/blankpage.jpg", // Placeholder for blank page thumbnail
		};

		setPdfPages([...pdfPages, newPageObj]);
	};

	// Add pages from another PDF
	const addPagesFromFile = async (file) => {
		if (!file || !pdfDoc) return;

		setIsLoading(true); // Set loading state
		const arrayBuffer = await file.arrayBuffer();
		const newPdf = await PDFDocument.load(arrayBuffer);

		// Load using pdfjs for rendering thumbnails
		const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
		const newPdfJsDoc = await loadingTask.promise;

		const newPages = await Promise.all(
			newPdf.getPages().map(async (page, index) => {
				const thumbnail = await renderThumbnail(newPdfJsDoc, index);
				return {
					id: pdfPages.length + index + 1,
					index: pdfPages.length + index,
					page,
					width: page.getWidth(),
					height: page.getHeight(),
					thumbnail,
				};
			})
		);

		setPdfPages([...pdfPages, ...newPages]);

		setIsLoading(false); // Reset loading state
	};

	const mergePDF2 = async () => {
		if (!pdfDoc) return;

		try {
			const newPdf = await PDFDocument.create();

			for (let i = 0; i < pdfPages.length; i++) {
				const pageData = pdfPages[i];

				// Extract the PDF document from the page object
				const srcDoc = pageData.page.doc;

				if (!srcDoc || !(srcDoc instanceof PDFDocument)) {
					console.error("Invalid PDF document at index:", i);
					continue;
				}

				// Use the existing page reference and copy it using the correct document
				const [copiedPage] = await newPdf.copyPages(srcDoc, [
					srcDoc.getPages().indexOf(pageData.page),
				]);

				newPdf.addPage(copiedPage);
			}

			const pdfBytes = await newPdf.save();
			const blob = new Blob([pdfBytes], { type: "application/pdf" });
			const url = URL.createObjectURL(blob);
			window.open(url);
		} catch (error) {
			console.error("Error exporting PDF:", error);
		}
	};

	const mergePDF = async () => {
		if (!pdfDoc) return;

		const start = Date.now();

		try {
			const newPdf = await PDFDocument.create();

			for (let i = 0; i < pdfPages.length; i++) {
				const pageData = pdfPages[i];

				// Extract the PDF document from the page object
				const srcDoc = pageData.page.doc;

				if (!srcDoc || !(srcDoc instanceof PDFDocument)) {
					console.error("Invalid PDF document at index:", i);
					continue;
				}

				// Copy the selected page
				const [copiedPage] = await newPdf.copyPages(srcDoc, [
					srcDoc.getPages().indexOf(pageData.page),
				]);

				newPdf.addPage(copiedPage);
			}

			const pdfBytes = await newPdf.save(); // ✅ success point
			const blob = new Blob([pdfBytes], { type: "application/pdf" });
			const url = URL.createObjectURL(blob);
			window.open(url);

			// optional: cleanup
			setTimeout(() => URL.revokeObjectURL(url), 60_000);

			// Analytics: estimate inputs
			const uniqueDocs = new Set();
			for (const p of pdfPages) {
				if (p?.page?.doc) uniqueDocs.add(p.page.doc);
			}

			// If you have the original File(s) somewhere, use their sizes instead.
			// Here we at least log pages count + output size.
			logOperationSuccess({
				operation: "ORGANIZE",
				page: "/pdf-organize", // set to your actual route
				fileCount: uniqueDocs.size || 1, // approx number of source PDFs
				totalBytes: blob.size, // output bytes (reliable)
				outputType: "pdf",
				durationMs: Date.now() - start,
			}).catch(console.error);
		} catch (error) {
			console.error("Error exporting PDF:", error);
		}
	};

	const renderThumbnail = async (pdfJsDoc, pageIndex) => {
		// if (!renderedPdf) return;

		console.log("here");
		const page = await pdfJsDoc.getPage(pageIndex + 1);
		const viewport = page.getViewport({ scale: 0.2 });

		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");
		canvas.height = viewport.height;
		canvas.width = viewport.width;

		await page.render({ canvasContext: context, viewport }).promise;
		// console.log(canvas.toDataURL("image/jpeg", 1));
		return canvas.toDataURL("image/jpeg", 1);
	};

	return (
		<div className="overflow-hidden pb-[10px] px-5">
			<TitleHeading title="Organize PDF Documents" />

			<div className="flex justify-between py-5">
				<div className=" flex gap-2">
					{pdfPages.length === 0 && (
						<AppFileSelect
							title="Click to Select File"
							onFileSelect={(data) => loadPDF(data)}
							acceptFileType={"application/pdf"}
						/>
					)}

					{pdfPages.length > 0 && (
						// <AppButton onChange={addPagesFromFile} title="Add Another PDF" />
						<AppFileSelect
							title="Add Another PDF File"
							accept="application/pdf"
							onFileSelect={(data) => addPagesFromFile(data)}
						/>
					)}

					{pdfPages.length > 0 && (
						<AppButton onClick={addBlankPage} title="Add Blank Page" />
					)}

					{isLoading && (
						<div className="flex gap-3 items-center">
							<AppSpinner />
							<p className="text-zinc-500 italic font-semibold">
								Loading... Pls wait
							</p>
						</div>
					)}
				</div>

				{pdfPages.length > 0 && (
					<div className="flex gap-2 text-slate-900">
						<AppButton
							title="Cancel"
							onClick={() => {
								setPdfPages([]);
								setPdfDoc(null);
							}}
						/>
						<AppButton title="Merge & Download PDF" onClick={mergePDF} />
					</div>
				)}
			</div>

			<hr className="border-[1px] border-gray-800 my-3" />

			{pdfPages.length === 0 && (
				<h1 className="text-center">
					<p className="text-zinc-500 text-lg font-semibold">
						No PDF Document available;
					</p>{" "}
					<p className="mt-2 text-sm text-zinc-500">Add PDF file to organize</p>
				</h1>
			)}

			<div
				className="text-center overflow-y-scroll no-scrollbar"
				style={{
					height: `${(98 / 100) * (height - 245)}px`,
					paddingBottom: "20px",
				}}
			>
				<div className={`relative`}>
					{pdfPages.map((box) => {
						const position = getPositionFromIndex(box.index);

						return (
							<Draggable
								key={box.id}
								position={{ x: position.x, y: position.y }}
								onStart={() => setDraggedBoxId(box.id)}
								onDrag={(e, data) => handleDrag(e, data, box.id)}
								onStop={handleStop}
								grid={[`${(80 / 100) * gridSize}`, `${(90 / 100) * gridSize}`]}
								// grid={[gridSize, gridSize]} // Snap movement to grid cells
							>
								<div
									style={{
										width: `${(80 / 100) * gridSize}px`,
										height: `${(95 / 100) * gridSize}px`,
										backgroundColor:
											draggedBoxId === box.id ? "wheat" : "white",
										color: "white",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										position: "absolute",
										borderRadius: "10px",
										cursor: "grab",
										transition: "all 0.4s ease-in-out", // Smooth animation for repositioning
									}}
								>
									<div
										style={{
											position: "relative",
											width: "100%",
											height: "100%",
										}}
									>
										<span
											onClick={(e) => {
												e.stopPropagation(); // Prevent triggering drag events
												handleDelete(box.id);
											}}
											className="absolute top-1 right-1 cursor-pointer"
										>
											<MdDelete color="tomato" size={20} />
										</span>
										<span className="flex justify-center items-end h-[100%]">
											<img
												src={box.thumbnail}
												alt={`Page ${box.id}`}
												className="w-full h-full object-contain rounded pointer-events-none"
											/>
										</span>

										<span className="absolute bottom-1 left-3 text-gray-700 text-xs">
											pg {box.id}
										</span>
									</div>
								</div>
							</Draggable>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default PDFOrganizer;
